import React from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const ScoreAndSurvey = ({
  score = 0,
  totalFeedback = 0,
  receivedFeedback = 0,
  percentage = 0,
}) => {
  const parseValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "null" || value === "undefined") return 0;
    const numValue = Number(value);
    if (isNaN(numValue) || !isFinite(numValue)) return 0;
    return numValue;
  };

  const safeScore = parseValue(score);
  const safeTotalFeedback = parseValue(totalFeedback);
  const safeReceivedFeedback = parseValue(receivedFeedback);
  const safePercentage = parseValue(percentage);
  const correctedReceivedFeedback = Math.min(safeReceivedFeedback, safeTotalFeedback);
  const calculatedPercentage = safeTotalFeedback > 0 && safePercentage === 0
    ? (correctedReceivedFeedback / safeTotalFeedback) * 100
    : safePercentage;
  const hasNoData = safeTotalFeedback === 0 && correctedReceivedFeedback === 0 && safeScore === 0 && calculatedPercentage === 0;

  const chartData = [
    { name: "Received", value: correctedReceivedFeedback, color: "#3b82f6" },
    { name: "Pending", value: Math.max(0, safeTotalFeedback - correctedReceivedFeedback), color: "#e5e7eb" },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="h-100">
      <Card className="h-100 border-0" style={{ borderRadius: "16px", overflow: "hidden", background: "#ffffff", boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)", border: "1px solid rgba(226, 232, 240, 0.8)" }}>
        <Card.Header className="pb-2 pt-2 px-3 bg-transparent border-0" style={{ borderBottom: "1px solid rgba(226, 232, 240, 0.8)" }}>
          <div className="d-flex align-items-center gap-3">
            <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)" }}>
              <FontAwesomeIcon icon={["fas", "chart-line"]} style={{ color: "#475569", fontSize: "16px" }} />
            </div>
            <div>
              <h5 className="mb-1 fw-bold" style={{ fontSize: "0.95rem", color: "#0f172a" }}>Performance Metrics</h5>
              <span className="d-block" style={{ fontSize: "0.75rem", color: "#64748b" }}>Score & Survey Analytics</span>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="pt-3 pb-3 px-3 d-flex flex-column" style={{ flexGrow: 1 }}>
          {hasNoData ? (
            <div className="xsurvey-no-data">
              <div className="xsurvey-no-data-icon"><FontAwesomeIcon icon={["fas", "chart-line"]} /></div>
              <p className="xsurvey-no-data-text">No data available</p>
            </div>
          ) : (
            <div className="d-flex flex-column h-100">
              <motion.div className="flex-grow-1" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                <div className="h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div style={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: "#64748b", flexShrink: 0 }} />
                      <span className="fw-semibold" style={{ fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>CSAT Score</span>
                    </div>
                    <div className="mb-2">
                      <span style={{ fontSize: "2rem", fontWeight: "700", color: "#0f172a", display: "block", lineHeight: "1.2", letterSpacing: "-0.03em" }}>{safeScore ? safeScore.toFixed(2) : "0.00"}</span>
                    </div>
                  </div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "0.625rem", padding: "0.625rem 1rem", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", width: "fit-content" }}>
                    <div style={{ width: "20px", height: "20px", borderRadius: "6px", backgroundColor: "#475569", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FontAwesomeIcon icon={["fas", "chart-line"]} style={{ color: "#ffffff", fontSize: "0.65rem" }} />
                    </div>
                    <span className="fw-semibold" style={{ fontSize: "0.75rem", color: "#334155" }}>Customer Satisfaction</span>
                  </div>
                </div>
              </motion.div>
              <div style={{ width: "100%", height: "1px", background: "#e2e8f0", margin: "1rem 0" }} />
              <motion.div className="flex-grow-1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
                <div className="h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div style={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: "#64748b", flexShrink: 0 }} />
                      <span className="fw-semibold" style={{ fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Survey Response</span>
                    </div>
                    <div className="d-flex align-items-baseline gap-1.5 mb-2">
                      <span style={{ fontSize: "2rem", fontWeight: "700", color: "#0f172a", display: "block", lineHeight: "1.2", letterSpacing: "-0.03em" }}>{correctedReceivedFeedback || 0}</span>
                      <span style={{ fontSize: "1.25rem", color: "#94a3b8", fontWeight: "500", lineHeight: "1.2" }}>/{safeTotalFeedback}</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.625rem", padding: "0.625rem 1rem", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", flex: 1 }}>
                      <div style={{ width: "20px", height: "20px", borderRadius: "6px", backgroundColor: "#475569", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FontAwesomeIcon icon={["fas", "check-circle"]} style={{ color: "#ffffff", fontSize: "0.65rem" }} />
                      </div>
                      <span className="fw-bold" style={{ fontSize: "0.8rem", color: "#334155" }}>{calculatedPercentage ? calculatedPercentage.toFixed(2) : "0.00"}%</span>
                      <span className="fw-medium" style={{ fontSize: "0.7rem", color: "#64748b" }}>Response Rate</span>
                    </div>
                    <div style={{ width: "48px", height: "48px", flexShrink: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={chartData} cx="50%" cy="50%" innerRadius={12} outerRadius={20} startAngle={90} endAngle={-270} dataKey="value">
                            {chartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={index === 0 ? "#3b82f6" : "#e2e8f0"} />))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
};

ScoreAndSurvey.propTypes = {
  score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalFeedback: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  receivedFeedback: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ScoreAndSurvey;
